import { lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { InlineWidget } from 'react-calendly';
import { ArrowLeft, Mail, Phone, MapPin } from 'lucide-react';
import { ShopifyBadge, MicrosoftBadge } from '../components/PartnerBadges';
import { SEO } from '../components/SEO';

export const BookingPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4] grid-bg flex flex-col">
      <SEO
        title="Book a Free Consultation"
        description="Book a free 30-minute consultation with Smart Solutions. We discuss your Shopify, software, IT, networking, or integration project — no commitment, just a conversation."
        path="/book"
      />
      {/* Top bar */}
      <div
        className="sticky top-0 z-50 border-b border-[#E2E8F0]"
        style={{ background: 'rgba(248, 247, 244, 0.95)', backdropFilter: 'blur(20px)' }}
      >
        <div className="container-custom py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors group"
          >
            <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
            Back to site
          </Link>
          <div className="flex items-center gap-1">
            <span className="font-display text-[1.2rem] font-bold text-[#0F172A]">Smart</span>
            <span className="font-display text-[1.2rem] font-bold text-[#2563EB]">Solutions</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB] pulse-dot" />
            <span className="font-mono text-[0.58rem] tracking-[0.2em] text-[#374151] uppercase">Available Now</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center min-h-[calc(100vh-57px)]">
      <div className="container-custom py-16 w-full">
        <div className="grid lg:grid-cols-[1fr_600px] gap-16 items-start">

          {/* Left — Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:sticky lg:top-24"
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="section-label">Free Consultation</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>

            <h1
              className="font-display font-bold text-[#0F172A] mb-3 leading-[1.05]"
              style={{ fontSize: 'clamp(2rem, 3.5vw, 2.8rem)' }}
            >
              Book a{' '}
              <span className="italic text-gold">Call</span>
              <br />with Us
            </h1>

            <p className="text-[#374151] leading-relaxed mb-8 text-sm max-w-sm">
              Pick a time that works for you. We'll discuss your project, understand your goals,
              and map out the best path forward — no commitment required.
            </p>

            {/* What to expect */}
            <div className="mb-8">
              <h3 className="font-mono text-[0.6rem] tracking-[0.22em] text-[#2563EB] uppercase mb-4">
                What to Expect
              </h3>
              <div className="space-y-3">
                {[
                  { num: '01', text: 'Tell us about your project and goals' },
                  { num: '02', text: 'We assess the best technology approach' },
                  { num: '03', text: 'Receive a clear plan and timeline' },
                  { num: '04', text: 'No pressure — decide at your own pace' },
                ].map(({ num, text }) => (
                  <div key={num} className="flex items-start gap-4">
                    <span className="font-mono text-[0.6rem] tracking-widest text-[#2563EB] w-5 shrink-0 mt-0.5">{num}</span>
                    <span className="text-[#374151] text-sm">{text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="divider-gold mb-7 opacity-50" />

            {/* Contact details */}
            <div className="space-y-4">
              {[
                { Icon: Mail,   label: 'Email',    value: 'hello@smartsolution.ai' },
                { Icon: Phone,  label: 'Phone',    value: '+965 6596 6786' },
                { Icon: MapPin, label: 'Location', value: 'Kuwait City, Kuwait' },
              ].map(({ Icon, label, value }) => (
                <div key={label} className="flex items-center gap-4">
                  <div className="w-8 h-8 flex items-center justify-center border border-[#E2E8F0] bg-white shrink-0">
                    <Icon size={14} style={{ color: '#2563EB' }} />
                  </div>
                  <div>
                    <div className="font-mono text-[0.52rem] tracking-widest text-[#9CA3AF] uppercase">{label}</div>
                    <div className="text-[#0F172A] text-sm font-medium">{value}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="divider-gold mt-7 mb-6 opacity-50" />

            {/* Partner badges */}
            <div className="flex flex-wrap gap-2">
              <ShopifyBadge />
              <MicrosoftBadge />
            </div>
          </motion.div>

          {/* Right — Calendly */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:pt-2"
          >
            <div className="border border-[#E2E8F0] bg-white shadow-sm overflow-hidden">
              <Suspense fallback={<div className="h-[660px] flex items-center justify-center">Loading calendar...</div>}>
                <InlineWidget
                  url="https://calendly.com/hello-smartsolution/30min"
                  styles={{ height: '660px' }}
                  pageSettings={{
                    backgroundColor: 'FFFFFF',
                    primaryColor: '4F46E5',
                    textColor: '1A1A2E',
                    hideEventTypeDetails: true,
                    hideLandingPageDetails: true,
                  }}
                />
              </Suspense>
            </div>
          </motion.div>

        </div>
      </div>
      </div>

      {/* Footer strip */}
      <div className="border-t border-[#E2E8F0] py-6">
        <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] uppercase">
            © {new Date().getFullYear()} Smart Solution · All Rights Reserved
          </p>
          <Link
            to="/"
            className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors"
          >
            Return to main site →
          </Link>
        </div>
      </div>
    </div>
  );
};
